import {
  anafAlerts,
  anafAsOf,
  cleanCui,
  cuiProblem,
  currencySign,
  fmtNum,
  lineValue,
  numInput,
  parseNum,
  poMoney,
  poTotals,
  type Company,
  type PoDraft,
  type PoKind,
  type PoLine,
  type Rfq,
  type Transport,
  t,
} from '@procumi/shared';
import { approvalChain, lei as leiText } from '@procumi/shared';
import { useEffect, useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { autoSendAllowed, currentUser } from '../lib/me';
import { useAsync } from '../hooks/useAsync';
import { legal } from '../lib/market';
import { confirmDiscard, Dialog } from './Dialog';
import { ErrorBox, Loading } from './StateBox';
import { DateInput } from './DateInput';

/**
 * Comanda de achiziție, înainte de emitere. Vine completată de server din
 * oferta aleasă (sau din ultima comandă anulată); omul corectează ce s-a
 * negociat și o emite. Emisă, nu se mai schimbă — se anulează și se reface.
 */

/** O poziție cum se scrie în formular: numerele rămân text până la emitere. */
interface LineDraft {
  key: number;
  desc: string;
  qty: string;
  unit: string;
  unitPrice: string;
  /** chirie și prețul din ofertă trec neatinse, de la ciornă la comandă */
  rent?: PoLine['rent'];
  offerPrice?: number;
}

interface ExtraDraft {
  key: number;
  label: string;
  amount: string;
}

let seq = 0;
const key = () => ++seq;

/** Ce e scris, ca ciornă tipată; mesajul primei probleme dacă ceva nu e număr. */
function readDraft(
  base: PoDraft,
  f: {
    lines: LineDraft[];
    extras: ExtraDraft[];
    transportCost: string;
    vatRate: string;
    fxRate: string;
  } & Pick<
    PoDraft,
    | 'supplierCui'
    | 'supplierAddress'
    | 'contactName'
    | 'contactEmail'
    | 'deliveryAddress'
    | 'deliveryDate'
    | 'deliveryTerm'
    | 'transport'
    | 'payment'
    | 'notes'
  >,
): PoDraft | string {
  const lines: PoDraft['lines'] = [];
  for (const [i, l] of f.lines.entries()) {
    const qty = parseNum(l.qty);
    const unitPrice = parseNum(l.unitPrice);
    if (!l.desc.trim()) return t('Poziția {0} nu are denumire.', i + 1);
    if (qty == null || qty <= 0)
      return t('Poziția {0}: cantitatea trebuie să fie mai mare decât zero.', i + 1);
    if (!l.unitPrice.trim())
      return t('Poziția {0} nu are preț: scrie prețul negociat sau șterge poziția.', i + 1);
    if (unitPrice == null || unitPrice <= 0)
      return t('Poziția {0}: prețul trebuie să fie un număr mai mare decât zero.', i + 1);
    lines.push({
      desc: l.desc.trim(),
      qty,
      unit: l.unit.trim() || t('buc'),
      unitPrice,
      ...(l.rent ? { rent: l.rent } : {}),
      ...(l.offerPrice != null ? { offerPrice: l.offerPrice } : {}),
    });
  }
  if (!lines.length) return t('Comanda are nevoie de cel puțin o poziție.');
  const extras: PoDraft['extras'] = [];
  for (const e of f.extras) {
    if (!e.label.trim() && !e.amount.trim()) continue;
    const amount = parseNum(e.amount);
    if (!e.label.trim() || amount == null || amount <= 0)
      return t('Fiecare cost are nevoie de ce e și de o sumă.');
    extras.push({ label: e.label.trim(), amount });
  }
  const transportCost = f.transport === 'extra' ? (parseNum(f.transportCost) ?? NaN) : 0;
  if (Number.isNaN(transportCost) || transportCost < 0) return t('Costul transportului nu e un număr.');
  const vatRate = parseNum(f.vatRate);
  if (vatRate == null || vatRate < 0 || vatRate > 100) return t('Cota de TVA nu e un procent.');
  const fxRate = base.currency === 'EUR' ? parseNum(f.fxRate) : null;
  if (base.currency === 'EUR' && (fxRate == null || fxRate < 1 || fxRate > 20)) {
    return t('Scrie cursul: câți lei face un euro.');
  }
  if (!f.deliveryAddress.trim()) return t('Scrie adresa de livrare.');
  // Furnizorul se poate adăuga doar cu nume și email; pe comandă trebuie și datele lui.
  if (!f.supplierCui.trim()) return t('Scrie {0}-ul furnizorului: apare pe comandă.', legal().taxId);
  if (!f.supplierAddress.trim()) return t('Scrie adresa sediului furnizorului: apare pe comandă.');
  return {
    ...base,
    supplierCui: f.supplierCui.trim(),
    supplierAddress: f.supplierAddress.trim(),
    contactName: f.contactName.trim(),
    contactEmail: f.contactEmail.trim(),
    deliveryAddress: f.deliveryAddress.trim(),
    deliveryDate: f.deliveryDate,
    deliveryTerm: f.deliveryTerm.trim(),
    fxRate,
    lines,
    transport: f.transport,
    transportCost,
    extras,
    vatRate,
    payment: f.payment.trim(),
    notes: f.notes.trim(),
  };
}

export function OrderDialog({
  rfq,
  kind = 'order',
  supplierId,
  toast,
  onIssued,
  onClose,
}: {
  rfq: Rfq;
  /** 'supplement' = suplimentare către furnizorul ales, după comanda din cerere */
  kind?: PoKind;
  /** la comanda împărțită: furnizorul părții; lipsă = furnizorul ales */
  supplierId?: string;
  toast: Toast;
  onIssued: (updated: Rfq) => void;
  onClose: () => void;
}) {
  const [state, setState] = useState<{ draft: PoDraft; company: Company } | null>(null);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api
      .orderDraft(rfq.id, kind, supplierId)
      .then(setState)
      .catch((e) => setError(errMsg(e)));
  }, [rfq.id, kind, supplierId]);

  return (
    <Dialog onClose={onClose} wide dirty={dirty}>
      {error ? (
        <>
          <div className="dlg-b">
            <ErrorBox error={error} />
          </div>
          <div className="dlg-f">
            <button className="btn" onClick={onClose}>
              {t('Închide')}
            </button>
          </div>
        </>
      ) : state ? (
        <OrderForm
          rfq={rfq}
          kind={kind}
          supplierId={supplierId ?? rfq.chosen!.supplier.id}
          base={state.draft}
          company={state.company}
          toast={toast}
          onDirty={setDirty}
          onIssued={onIssued}
          onClose={onClose}
        />
      ) : (
        <div className="dlg-b">
          <Loading />
        </div>
      )}
    </Dialog>
  );
}

function OrderForm({
  rfq,
  kind,
  supplierId,
  base,
  company,
  toast,
  onDirty,
  onIssued,
  onClose,
}: {
  rfq: Rfq;
  kind: PoKind;
  supplierId: string;
  base: PoDraft;
  company: Company;
  toast: Toast;
  onDirty: (dirty: boolean) => void;
  onIssued: (updated: Rfq) => void;
  onClose: () => void;
}) {
  const send = rfq.sends.find((s) => s.supplier.id === supplierId);
  const supplier = send?.supplier.name ?? rfq.chosen!.supplier.name;
  // starea la ANAF a furnizorului ales: o comandă către o firmă inactivă se vede înainte de emitere
  const anaf = send?.supplier.anaf;
  const anafBad = anafAlerts(anaf).filter((a) => a.level === 'danger');
  const sign = currencySign(base.currency);
  const [f, setF] = useState(() => ({
    supplierCui: base.supplierCui,
    supplierAddress: base.supplierAddress,
    contactName: base.contactName,
    contactEmail: base.contactEmail,
    deliveryAddress: base.deliveryAddress,
    deliveryDate: base.deliveryDate,
    deliveryTerm: base.deliveryTerm,
    transport: base.transport,
    transportCost: base.transportCost ? numInput(base.transportCost) : '',
    vatRate: numInput(base.vatRate),
    fxRate: base.fxRate ? numInput(base.fxRate) : '',
    payment: base.payment,
    notes: base.notes,
    lines: base.lines.map(
      (l): LineDraft => ({
        key: key(),
        desc: l.desc,
        // la suplimentare, cantitatea o scrie omul: cât mai are nevoie
        qty: l.qty > 0 ? numInput(l.qty) : '',
        unit: l.unit,
        rent: l.rent,
        offerPrice: l.offerPrice,
        // Poziția pe care furnizorul n-a cotat-o vine cu 0: caseta rămâne goală,
        // ca omul să vadă că lipsește, nu să emită o comandă cu 0 lei pe ea.
        unitPrice: l.unitPrice > 0 ? numInput(l.unitPrice) : '',
      }),
    ),
    extras: base.extras.map((e) => ({ key: key(), label: e.label, amount: numInput(e.amount) })),
  }));
  const [buyer, setBuyer] = useState({
    cui: company.cui,
    regCom: company.regCom,
    address: company.address,
  });
  const [busy, setBusy] = useState(false);
  // CUI-ul sau adresa furnizorului lipseau din agendă: se cer aici și rămân acolo
  const [supplierMissing] = useState(() => !base.supplierCui.trim() || !base.supplierAddress.trim());
  const [anafBusy, setAnafBusy] = useState(false);
  // Datele firmei tale lipsesc (firmă nouă): se cer aici, sus, la prima comandă (Ticket 08, P9)
  const [buyerFirst] = useState(() => !company.cui.trim() || !company.address.trim());
  const [buyerAnafBusy, setBuyerAnafBusy] = useState(false);
  const [initial] = useState(() => JSON.stringify([f, buyer]));
  const approvals = useAsync(() => api.approvals(), [], 'approvals');
  const dirty = JSON.stringify([f, buyer]) !== initial;
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const setLine = (k: number, field: keyof Omit<LineDraft, 'key'>, v: string) =>
    setF((x) => ({ ...x, lines: x.lines.map((l) => (l.key === k ? { ...l, [field]: v } : l)) }));
  const setExtra = (k: number, field: 'label' | 'amount', v: string) =>
    setF((x) => ({ ...x, extras: x.extras.map((e) => (e.key === k ? { ...e, [field]: v } : e)) }));

  // Totalurile se arată pe loc, din ce se poate citi; rândurile încă greșite contează zero.
  const preview = poTotals({
    lines: f.lines.map((l) => ({
      desc: l.desc,
      unit: l.unit,
      qty: parseNum(l.qty) ?? 0,
      unitPrice: parseNum(l.unitPrice) ?? 0,
    })),
    transport: f.transport,
    transportCost: parseNum(f.transportCost) ?? 0,
    extras: f.extras.map((e) => ({ label: e.label, amount: parseNum(e.amount) ?? 0 })),
    vatRate: parseNum(f.vatRate) ?? 0,
  });
  const fx = base.currency === 'EUR' ? parseNum(f.fxRate) : null;
  const buyerMissing = !buyer.cui.trim() || !buyer.address.trim();

  const cancel = () => {
    void confirmDiscard(dirty).then((ok) => ok && onClose());
  };

  // Cine aprobă comanda, dacă trece de prag: arătat pe buton, înainte de emitere.
  const me = currentUser();
  // Comanda împărțită trece pragurile cu totalul întregii împărțiri, ca pe server.
  const split = kind === 'order' && rfq.chosen?.split?.length ? (rfq.chosen.splitTotal ?? 0) : 0;
  const value = Math.max(preview.net * (base.currency === 'EUR' ? (fx ?? 0) : 1), split);
  const site = approvals.data?.sites.find((s) => s.id === rfq.site.id);
  const chain = approvals.data
    ? approvalChain(value, approvals.data.steps, approvals.data.level, site?.approverId ?? null).filter(
        (s) => !(me.role === 'approver' && (s.approverId == null || s.approverId === me.id)),
      )
    : [];
  const toApproval = chain.length > 0;
  const buyerChanged =
    buyer.cui !== company.cui || buyer.regCom !== company.regCom || buyer.address !== company.address;
  // Fără aprobare și cu emailul furnizorului, comanda pleacă din Procumi la generare.
  const canSend = autoSendAllowed() && !toApproval && f.contactEmail.trim() !== '';

  /** CUI bun și adresă goală: adresa sediului vine de la ANAF, ca la „Furnizor nou”. */
  const anafAddress = async () => {
    const cui = f.supplierCui.trim();
    if (!legal().checkCui || !cui || cuiProblem(cui) !== '' || f.supplierAddress.trim() || anafBusy) return;
    setAnafBusy(true);
    try {
      const info = await api.anafLookup(cleanCui(cui));
      if (info.found && info.address) {
        setF((x) => (x.supplierAddress.trim() ? x : { ...x, supplierAddress: info.address }));
      }
    } catch {
      // ANAF tace: adresa se scrie de mână
    } finally {
      setAnafBusy(false);
    }
  };

  /** La fel pentru firma ta: CUI-ul bun aduce adresa sediului și nr. de la Registrul Comerțului. */
  const buyerAnaf = async () => {
    const cui = buyer.cui.trim();
    if (!legal().checkCui || !cui || cuiProblem(cui) !== '' || buyer.address.trim() || buyerAnafBusy) return;
    setBuyerAnafBusy(true);
    try {
      const info = await api.anafLookup(cleanCui(cui));
      if (info.found) {
        setBuyer((x) => ({
          ...x,
          address: x.address.trim() ? x.address : info.address,
          regCom: x.regCom.trim() ? x.regCom : info.regCom,
        }));
      }
    } catch {
      // ANAF tace: se scriu de mână
    } finally {
      setBuyerAnafBusy(false);
    }
  };

  const issue = async (send: boolean) => {
    const draft = readDraft(base, f);
    if (typeof draft === 'string') {
      toast(draft);
      return;
    }
    if (buyerMissing) {
      toast(
        me.isAdmin
          ? t('Completează {0}-ul și adresa sediului firmei tale: apar pe comandă.', legal().taxId)
          : t('Datele firmei le completează un administrator, în „Contul firmei”.'),
      );
      return;
    }
    if (buyerChanged && !me.isAdmin) {
      toast(t('Datele firmei le completează un administrator, în „Contul firmei”.'));
      return;
    }
    setBusy(true);
    try {
      // Datele firmei se scriu o dată și rămân pentru comenzile următoare.
      if (buyerChanged) {
        await api.updateCompany({
          cui: buyer.cui.trim(),
          regCom: buyer.regCom.trim(),
          address: buyer.address.trim(),
        });
      }
      const updated = await api.issueOrder(rfq.id, { ...draft, kind, supplierId }, send);
      onIssued(updated);
      onClose();
      const made = updated.orders?.filter((o) => o.supplier.id === supplierId && o.kind === kind).at(-1);
      toast(
        toApproval
          ? t('Comanda a plecat la aprobare. Primești un email când e aprobată.')
          : made?.sentAt
            ? t('Comanda {0} a plecat la {1}', made.number, supplier)
            : made?.mailError
              ? t('Comanda {0} e generată, dar mailul n-a plecat: {1}', made.number, made.mailError)
              : t('Comanda {0} e generată. N-a plecat încă la furnizor.', made?.number ?? ''),
      );
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const buyerGroup = (
    <fieldset className="of-group">
      <legend>{t('Firma ta, pe comandă')}</legend>
      {buyerMissing ? (
        <p className="read-warn" style={{ margin: 0 }}>
          {me.isAdmin
            ? t(
                'Prima comandă: completează {0}-ul și adresa sediului firmei tale. Apar pe comandă și rămân pentru următoarele.{1}',
                legal().taxId,
                legal().checkCui ? t(' Adresa vine de la ANAF, după CUI.') : '',
              )
            : t('Datele firmei le completează un administrator, în „Contul firmei”.')}
        </p>
      ) : null}
      <div className="row">
        <label className="f">
          {legal().taxId}
          <input
            className="inp"
            value={buyer.cui}
            onChange={(e) => setBuyer({ ...buyer, cui: e.target.value })}
            onBlur={() => void buyerAnaf()}
            placeholder={legal().checkCui ? 'RO12345678' : undefined}
            aria-invalid={buyerMissing && !buyer.cui.trim()}
            maxLength={20}
          />
        </label>
        <label className="f">
          {legal().reg}
          <input
            className="inp"
            value={buyer.regCom}
            onChange={(e) => setBuyer({ ...buyer, regCom: e.target.value })}
            placeholder={legal().checkCui ? 'J08/123/2020' : undefined}
            maxLength={40}
          />
        </label>
      </div>
      <label className="f">
        {t('Adresa sediului')}
        <input
          className="inp"
          value={buyer.address}
          onChange={(e) => setBuyer({ ...buyer, address: e.target.value })}
          placeholder={buyerAnafBusy ? t('Caut la ANAF…') : undefined}
          aria-invalid={buyerMissing && !buyer.address.trim()}
          maxLength={300}
        />
      </label>
    </fieldset>
  );

  return (
    <>
      <div className="dlg-h">
        <div>
          <h2>
            {kind === 'supplement' ? t('Suplimentare către {0}', supplier) : t('Comandă către {0}', supplier)}
          </h2>
          <p className="hint">
            {kind === 'supplement'
              ? t(
                  'Mai comanzi de la același furnizor, pe același proiect. Pozițiile din ofertă au prețul ei; scrie cantitățile, scoate ce nu-ți trebuie, adaugă poziții noi cu prețul negociat. Fără concurență: nu intră la diferența obținută.',
                )
              : t(
                  'Completată din oferta aleasă. Corectează ce s-a negociat; generată, comanda primește număr și nu se mai schimbă.',
                )}
          </p>
        </div>
      </div>
      <div className="dlg-b order-form">
        {buyerFirst ? buyerGroup : null}
        <fieldset className="of-group">
          <legend>{t('Furnizor')}</legend>
          {anafBad.length ? (
            <p className="anaf-warn" role="alert">
              {anafBad.map((a) => a.text).join(' ')} ({anafAsOf(anaf!)})
            </p>
          ) : null}
          {supplierMissing ? (
            <p className="read-warn" style={{ margin: 0 }}>
              {t(
                'Pentru comandă trebuie {0}-ul și adresa sediului furnizorului. Scrie-le aici: rămân în agendă pentru comenzile următoare.{1}',
                legal().taxId,
                legal().checkCui ? t(' Adresa vine de la ANAF, după CUI.') : '',
              )}
            </p>
          ) : null}
          <div className="row">
            <label className="f">
              {legal().taxId}
              <input
                className="inp"
                value={f.supplierCui}
                onChange={(e) => set('supplierCui', e.target.value)}
                onBlur={() => void anafAddress()}
                placeholder={legal().checkCui ? 'RO12345678' : undefined}
                aria-invalid={supplierMissing && !f.supplierCui.trim()}
                maxLength={20}
              />
            </label>
            <label className="f">
              {t('Adresă sediu')}
              <input
                className="inp"
                value={f.supplierAddress}
                onChange={(e) => set('supplierAddress', e.target.value)}
                placeholder={anafBusy ? t('Caut la ANAF…') : undefined}
                aria-invalid={supplierMissing && !f.supplierAddress.trim()}
                maxLength={300}
              />
            </label>
          </div>
          <div className="row">
            <label className="f">
              {t('Persoană de contact')}
              <input
                className="inp"
                value={f.contactName}
                onChange={(e) => set('contactName', e.target.value)}
                maxLength={120}
              />
            </label>
            <label className="f">
              {t('Email')}
              <input
                className="inp"
                type="email"
                value={f.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
                maxLength={200}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="of-group">
          <legend>{t('Livrare')}</legend>
          <label className="f">
            {t('Adresa de livrare')}
            <input
              className="inp"
              value={f.deliveryAddress}
              onChange={(e) => set('deliveryAddress', e.target.value)}
              maxLength={300}
            />
          </label>
          <div className="row">
            <label className="f">
              {t('Data dorită')}
              <DateInput value={f.deliveryDate} onChange={(v) => set('deliveryDate', v)} />
            </label>
            <label className="f">
              {t('Termen din ofertă')}
              <input
                className="inp"
                value={f.deliveryTerm}
                onChange={(e) => set('deliveryTerm', e.target.value)}
                maxLength={200}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="of-group">
          <legend>
            {t('Poziții')} <span className="muted">{t('· prețuri în {0}, fără TVA', sign)}</span>
          </legend>
          <div className="of-lines" role="table" aria-label={t('Pozițiile comenzii')}>
            <div className="of-line of-head" role="row">
              <span role="columnheader">{t('Denumire')}</span>
              <span role="columnheader">{t('Cantitate')}</span>
              <span role="columnheader">{t('UM')}</span>
              <span role="columnheader">{t('Preț unitar')}</span>
              <span role="columnheader" className="n">
                {t('Valoare')}
              </span>
              <span />
            </div>
            {f.lines.map((l, i) => {
              const qty = parseNum(l.qty);
              const price = parseNum(l.unitPrice);
              return (
                <div className="of-line" role="row" key={l.key}>
                  <input
                    className="inp"
                    aria-label={t('Denumire, poziția {0}', i + 1)}
                    value={l.desc}
                    onChange={(e) => setLine(l.key, 'desc', e.target.value)}
                    maxLength={300}
                  />
                  <input
                    className="inp n"
                    inputMode="decimal"
                    aria-label={t('Cantitate, poziția {0}', i + 1)}
                    value={l.qty}
                    onChange={(e) => setLine(l.key, 'qty', e.target.value)}
                  />
                  <input
                    className="inp"
                    aria-label={t('Unitate, poziția {0}', i + 1)}
                    value={l.unit}
                    onChange={(e) => setLine(l.key, 'unit', e.target.value)}
                    maxLength={20}
                  />
                  <input
                    className="inp n"
                    inputMode="decimal"
                    aria-label={t('Preț unitar, poziția {0}', i + 1)}
                    value={l.unitPrice}
                    onChange={(e) => setLine(l.key, 'unitPrice', e.target.value)}
                  />
                  <span className="of-val n">
                    {qty != null && price != null
                      ? fmtNum(lineValue({ desc: '', unit: '', qty, unitPrice: price }))
                      : '—'}
                  </span>
                  <button
                    className="of-x"
                    aria-label={t('Scoate poziția {0}', i + 1)}
                    title={t('Scoate poziția')}
                    disabled={f.lines.length === 1}
                    onClick={() => setF((x) => ({ ...x, lines: x.lines.filter((y) => y.key !== l.key) }))}
                  >
                    ×
                  </button>
                  {l.offerPrice != null && price != null && Math.abs(price - l.offerPrice) >= 0.005 ? (
                    <span className="of-note warn">
                      {t(
                        'În ofertă: {0} {1}. Comanzi cu {2}% {3}.',
                        fmtNum(l.offerPrice),
                        sign,
                        fmtNum(Math.round(((price - l.offerPrice) / l.offerPrice) * 1000) / 10),
                        price > l.offerPrice ? t('mai scump') : t('mai ieftin'),
                      )}
                    </span>
                  ) : null}
                  {l.rent ? (
                    <span className="of-note">
                      {t(
                        'Chirie: se plătește pe zilele reale de pe șantier, din avize{0}. Cantitatea de aici e estimarea.',
                        l.rent.minDays ? t(', minim {0} de zile', l.rent.minDays) : '',
                      )}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
          <button
            className="link"
            onClick={() =>
              setF((x) => ({
                ...x,
                lines: [...x.lines, { key: key(), desc: '', qty: '', unit: t('buc'), unitPrice: '' }],
              }))
            }
          >
            {t('+ Adaugă poziție')}
          </button>
        </fieldset>

        <fieldset className="of-group">
          <legend>{t('Transport și alte costuri')}</legend>
          <div className="row">
            <label className="f">
              {t('Transport')}
              <select
                className="inp"
                value={f.transport}
                onChange={(e) => set('transport', e.target.value as Transport)}
              >
                <option value="">{t('Nu e trecut pe comandă')}</option>
                <option value="included">{t('Inclus în prețuri')}</option>
                <option value="extra">{t('Se plătește separat')}</option>
              </select>
            </label>
            {f.transport === 'extra' ? (
              <label className="f">
                {t('Cost transport ({0})', sign)}
                <input
                  className="inp"
                  inputMode="decimal"
                  value={f.transportCost}
                  onChange={(e) => set('transportCost', e.target.value)}
                />
              </label>
            ) : null}
          </div>
          {f.extras.map((e) => (
            <div className="extra-row" key={e.key}>
              <label className="f">
                {t('Alt cost')}
                <input
                  className="inp"
                  value={e.label}
                  onChange={(ev) => setExtra(e.key, 'label', ev.target.value)}
                  maxLength={80}
                />
              </label>
              <label className="f">
                {t('Sumă ({0})', sign)}
                <input
                  className="inp"
                  inputMode="decimal"
                  value={e.amount}
                  onChange={(ev) => setExtra(e.key, 'amount', ev.target.value)}
                />
              </label>
              <button
                className="btn small"
                onClick={() => setF((x) => ({ ...x, extras: x.extras.filter((y) => y.key !== e.key) }))}
              >
                {t('Scoate')}
              </button>
            </div>
          ))}
          <button
            className="link"
            onClick={() =>
              setF((x) => ({ ...x, extras: [...x.extras, { key: key(), label: '', amount: '' }] }))
            }
          >
            {t('+ Alt cost pe comandă')}
          </button>
        </fieldset>

        <fieldset className="of-group">
          <legend>{t('Condiții')}</legend>
          <div className="row">
            <label className="f">
              {t('Plată')}
              <input
                className="inp"
                value={f.payment}
                onChange={(e) => set('payment', e.target.value)}
                placeholder={t('30 de zile de la livrare')}
                maxLength={200}
              />
            </label>
            <label className="f">
              {t('TVA (%)')}
              <input
                className="inp"
                inputMode="decimal"
                value={f.vatRate}
                onChange={(e) => set('vatRate', e.target.value)}
              />
            </label>
            {base.currency === 'EUR' ? (
              <label className="f">
                {t('Curs (lei pentru 1 €)')}
                <input
                  className="inp"
                  inputMode="decimal"
                  value={f.fxRate}
                  onChange={(e) => set('fxRate', e.target.value)}
                />
              </label>
            ) : null}
          </div>
          <label className="f">
            {t('Mențiuni')}
            <textarea
              className="inp"
              rows={2}
              value={f.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder={t('descărcare cu macaraua furnizorului, livrare între 8 și 16')}
              maxLength={2000}
            />
          </label>
        </fieldset>

        {buyerFirst ? null : buyerGroup}
      </div>

      <div className="of-totals">
        <dl>
          <div>
            <dt>{t('Total fără TVA')}</dt>
            <dd>{poMoney(preview.net, base.currency)}</dd>
          </div>
          <div>
            <dt>{t('TVA {0}%', f.vatRate || 0)}</dt>
            <dd>{poMoney(preview.vat, base.currency)}</dd>
          </div>
          <div className="gross">
            <dt>{t('Total cu TVA')}</dt>
            <dd>
              {poMoney(preview.gross, base.currency)}
              {fx ? (
                <span className="muted"> {t('· {0} lei', fmtNum(Math.round(preview.gross * fx)))}</span>
              ) : null}
            </dd>
          </div>
        </dl>
        <div className="of-acts">
          <button className="btn" onClick={cancel}>
            {t('Anulează')}
          </button>
          {toApproval ? (
            <span className="of-approval">
              {t(
                'Peste {0}: merge întâi la aprobare{1}.',
                leiText(chain[0]!.minValue),
                chain[0]!.approverId
                  ? ` (${approvals.data?.approvers.find((a) => a.id === chain[0]!.approverId)?.name ?? t('aprobator')})`
                  : '',
              )}
            </span>
          ) : null}
          {canSend ? (
            <button className="btn" disabled={busy} onClick={() => void issue(false)}>
              {t('Doar generează, o trimit eu')}
            </button>
          ) : null}
          <button className="btn primary" disabled={busy} onClick={() => void issue(canSend)}>
            {busy
              ? t('Se generează…')
              : toApproval
                ? t('Trimite la aprobare')
                : canSend
                  ? kind === 'supplement'
                    ? t('Generează și trimite suplimentarea')
                    : t('Generează și trimite furnizorului')
                  : kind === 'supplement'
                    ? t('Generează suplimentarea')
                    : t('Generează comanda')}
          </button>
        </div>
      </div>
    </>
  );
}
